import { ref } from 'vue'

export interface TimeSlot {
  startTime: string
  endTime: string
}

export interface TaxSettings {
  mode1?: {
    eatHere: number
    takeAway: number
  }
  mode2?: {
    selfOrderingAtTable: number
  }
  mode5?: {
    eatHere: number
    takeAway: number
  }
}

export interface LocalizedContent {
  name: string
  description: string
}

export interface PriceLevel {
  id: string
  label: string
  startTime: string
  endTime: string
  daysOfWeek: ('Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday')[]
}

export interface Article {
  id: string
  restaurantId: string
  name: string
  description: string
  price: number
  mainCategory: string
  subCategory: string
  subSubCategory?: string
  image?: string
  available: boolean
  isFrozen?: boolean
  position?: number
  quickOrderNumber?: string
  preparationTime: number
  properties: string[]
  allergens: string[]
  additives: string[]
  foodCategory: 'vegetarian' | 'vegan' | 'non-vegetarian' | 'gluten-free'
  foodOptions: string[]
  showInDigitalMenu: boolean
  useCustomizedOfferingTime: boolean
  customizedOfferingTime: Record<string, TimeSlot[]>
  taxes: TaxSettings
  createdAt: string
  localizedContent: Record<string, LocalizedContent>
  levelPrices: Record<string, number>
}

export interface ArticleDraft {
  id?: string
  name: string
  description: string
  price: number
  mainCategory: string
  subCategory: string
  subSubCategory?: string
  image?: string
  available: boolean
  isFrozen?: boolean
  position?: number
  quickOrderNumber?: string
  preparationTime: number
  properties: string[]
  allergens: string[]
  additives: string[]
  foodCategory: 'vegetarian' | 'vegan' | 'non-vegetarian' | 'gluten-free'
  foodOptions: string[]
  showInDigitalMenu: boolean
  useCustomizedOfferingTime: boolean
  customizedOfferingTime: Record<string, TimeSlot[]>
  taxes: TaxSettings
  localizedContent: Record<string, LocalizedContent>
  levelPrices: Record<string, number>
}

const createEmptyDraft = (): ArticleDraft => ({
  name: '',
  description: '',
  price: 0,
  mainCategory: '',
  subCategory: '',
  subSubCategory: '',
  image: '',
  available: true,
  isFrozen: false,
  position: 1,
  quickOrderNumber: '',
  preparationTime: 0,
  properties: [],
  allergens: [],
  additives: [],
  foodCategory: 'non-vegetarian',
  foodOptions: [],
  showInDigitalMenu: true,
  useCustomizedOfferingTime: false,
  customizedOfferingTime: {
    Monday: [],
    Tuesday: [],
    Wednesday: [],
    Thursday: [],
    Friday: [],
    Saturday: [],
    Sunday: [],
  },
  taxes: {},
  localizedContent: {},
  levelPrices: {},
})

export const useArticles = () => {
  // Store the raw API response as-is (category tree structure)
  const articles = ref<any>(null)
  // Matrix-disabled + payment-enabled gate for the missing-tax badge/count/filter (see RestaurantMenuItemService::taxGateEnabled)
  const taxGateEnabled = ref(false)
  const missingTaxCount = ref(0)

  const draft = ref<ArticleDraft>(createEmptyDraft())

  const addArticle = (article: Omit<Article, 'id' | 'createdAt'>) => {
    const newArticle: Article = {
      ...article,
      id: Date.now().toString(),
      createdAt: new Date().toISOString().split('T')[0],
    }
    return newArticle
  }

  const updateArticle = (id: string, updates: Partial<Article>) => {
    // Placeholder
  }

  const deleteArticle = (id: string) => {
    // Placeholder
  }

  const deleteMenuItem = async (restaurantId: string, itemId: string, apiInstance: any): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      const response = await apiInstance.delete(
        `/restaurant/${restaurantId}/menu-items/${itemId}`
      )

      const message = response.data?.message || 'Menu item deleted successfully'
      return { success: true, message }
    } catch (error: any) {
      console.error('❌ Error deleting menu item:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete menu item'
      return { success: false, error: errorMessage }
    }
  }

  const bulkDeleteMenuItems = async (restaurantId: string, itemIds: string[], apiInstance: any): Promise<{ success: boolean; message?: string; error?: string }> => {
    try {
      const response = await apiInstance.post(
        `/restaurant/${restaurantId}/menu-items/bulk-delete`,
        { item_ids: itemIds }
      )

      const message = response.data?.message || `${itemIds.length} menu items deleted successfully`
      return { success: true, message }
    } catch (error: any) {
      console.error('❌ Error bulk deleting menu items:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete menu items'
      return { success: false, error: errorMessage }
    }
  }

  const getArticle = (id: string) => {
    // Placeholder
    return null
  }

  const getRestaurantArticles = (restaurantId: string) => {
    return []
  }

  const toggleDigitalMenu = async (restaurantId: string, id: string, currentValue: number, apiInstance: any): Promise<boolean> => {
    try {
      const newValue = currentValue == 1 ? 0 : 1
       await apiInstance.post(`/restaurant/${restaurantId}/menu-items/digital-menu-status`, {
          items: [id],
          digital_menu_activate: newValue
        })
      return true
    } catch (error: any) {
      console.error('❌ Error toggling digital menu:', error)
      throw error
    }
  }

  const toggleFrozen = async (restaurantId: string, id: string, isFrozen: number, apiInstance: any, pinNumber?: string): Promise<boolean> => {
    try {
      // Determine the new frozen state from the current state
      const currentFrozenState = isFrozen === 1
      const newFrozenState = !currentFrozenState
      const action = newFrozenState ? 'freeze' : 'unfreeze'
      const pinNum = pinNumber || null

      // Call the API to update the server
      const response = await apiInstance.post(
        `/restaurant/${restaurantId}/menu-items/bulk-freeze`,
        {
          items: [id],
          action: action,
          rest_pin_number: pinNum
        }
      )

      // State is already updated at the source (in the component) via Vue's reactivity
      // No need to search the tree for the item

      return true
    } catch (error: any) {
      console.error('❌ Error toggling frozen state:', error)
      throw error
    }
  }

  const copyArticle = (id: string) => {
    // Placeholder
  }

  const moveArticlePosition = (id: string, direction: 'up' | 'down') => {
    // Placeholder
  }

  const initializeDraft = (articleId?: string) => {
    draft.value = createEmptyDraft()
  }

  const updateDraft = (updates: Partial<ArticleDraft>) => {
    draft.value = { ...draft.value, ...updates }
  }

  const getDraft = () => {
    return draft.value
  }

  const clearDraft = () => {
    draft.value = createEmptyDraft()
  }

  const reorderArticlesInSubcategory = (category: string, subcategory: string, orderedIds: string[]) => {
    // Placeholder
  }

  const reorderArticles = (orderedIds: string[]) => {
    // Placeholder
  }

  const saveArticlesOrder = () => {
    // Placeholder
  }

  const loadMenuItemsFromAPI = async (restaurantId: string, apiInstance: any): Promise<void> => {
    try {
      const response = await apiInstance.get(`/restaurant/${restaurantId}/menu-items`)
      const data = response.data.data

      if (!Array.isArray(data)) {
        console.warn('⚠️ API response is not an array:', data)
        return
      }

      // Store the raw API response as-is
      articles.value = data
      taxGateEnabled.value = response.data.tax_gate_enabled === true
      missingTaxCount.value = response.data.missing_tax_count ?? 0
    } catch (error: any) {
      console.error('❌ Error loading menu items from API:', error)
      throw new Error(error.response?.data?.message || error.message || 'Failed to load menu items from API')
    }
  }

  const getMenuItemById = async (restaurantId: string, itemId: string, apiInstance: any): Promise<{ success: boolean; data?: any; error?: string }> => {
    try {
      const response = await apiInstance.get(`/restaurant/${restaurantId}/menu-items/${itemId}`)

      if (response.data?.error === false && response.data?.data) {
        return { success: true, data: response.data.data }
      } else {
        console.warn('⚠️ Menu item not found or error in response:', response.data)
        return { success: false, error: response.data?.message || 'Menu item not found' }
      }
    } catch (error: any) {
      console.error('❌ Error fetching menu item:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch menu item'
      return { success: false, error: errorMessage }
    }
  }

  const createMenuItem = async (restaurantId: string, payload: any, apiInstance: any): Promise<{ success: boolean; message?: string; errors?: Record<string, string[]> }> => {
    try {
      const response = await apiInstance.post(`/restaurant/${restaurantId}/menu-items`, payload)

      // Status code 200 indicates success
      if (response.status === 200) {
        const message = response.data?.message || 'Menu item created successfully'
        return { success: true, message }
      } else {
        console.warn('⚠️ Unexpected response status:', response.status)
        return { success: false, errors: { general: ['Unexpected response from server'] } }
      }
    } catch (error: any) {
      // Handle validation errors (422)
      if (error.response?.status === 422) {
        const errorData = error.response.data
        console.warn('⚠️ Validation error:', errorData.errors)
        return { success: false, errors: errorData.errors || {} }
      }

      // Handle other errors
      console.error('❌ Error creating menu item:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create menu item'
      return { success: false, errors: { general: errorMessage } }
    }
  }

  const updateMenuItem = async (restaurantId: string, itemId: string, payload: any, apiInstance: any): Promise<{ success: boolean; message?: string; errors?: Record<string, string[]> }> => {
    try {
      const response = await apiInstance.put(`/restaurant/${restaurantId}/menu-items/${itemId}`, payload)

      // Status code 200 indicates success
      if (response.status === 200) {
        const message = response.data?.message || 'Menu item updated successfully'
        return { success: true, message }
      } else {
        console.warn('⚠️ Unexpected response status:', response.status)
        return { success: false, errors: { general: ['Unexpected response from server'] } }
      }
    } catch (error: any) {
      // Handle validation errors (422)
      if (error.response?.status === 422) {
        const errorData = error.response.data
        console.warn('⚠️ Validation error:', errorData.errors)
        return { success: false, errors: errorData.errors || {} }
      }

      // Handle other errors
      console.error('❌ Error updating menu item:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update menu item'
      return { success: false, errors: { general: errorMessage } }
    }
  }

  const transformAPIItemToArticle = (item: any, restaurantId: string): Article => {
    // Not used anymore - storing API data as-is
    return {} as Article
  }

  return {
    articles,
    taxGateEnabled,
    missingTaxCount,
    draft,
    addArticle,
    updateArticle,
    deleteArticle,
    getArticle,
    getRestaurantArticles,
    toggleFrozen,
    toggleDigitalMenu,
    copyArticle,
    moveArticlePosition,
    reorderArticles,
    reorderArticlesInSubcategory,
    initializeDraft,
    updateDraft,
    getDraft,
    clearDraft,
    saveArticlesOrder,
    loadMenuItemsFromAPI,
    getMenuItemById,
    createMenuItem,
    updateMenuItem,
    deleteMenuItem,
    bulkDeleteMenuItems,
    transformAPIItemToArticle,
  }
}
